import React, { useEffect } from 'react';

const DisablePullToRefresh = () => {
  useEffect(() => {
    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    // Function to check if an element is scrollable
    const isScrollable = (element) => {
      const hasScrollableContent = element.scrollHeight > element.clientHeight;
      const overflowY = window.getComputedStyle(element).overflowY;
      return hasScrollableContent && (overflowY === 'scroll' || overflowY === 'auto');
    };

    // Function to find the nearest scrollable parent
    const findScrollableParent = (element) => {
      let parent = element;
      while (parent && parent !== document.body) {
        if (isScrollable(parent)) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const handleTouchStart = (event) => {
      const touch = event.touches[0];
      startY = touch.clientY;
      currentY = touch.clientY;
      isDragging = true;
    };

    const handleTouchMove = (event) => {
      if (!isDragging) return;

      const touch = event.touches[0];
      currentY = touch.clientY;
      const deltaY = currentY - startY;

      // Find if the touch is within a scrollable container
      const scrollableParent = findScrollableParent(event.target);
      
      // If there's a scrollable parent, let it handle the scroll
      if (scrollableParent) {
        // Check if the scrollable parent is at the top and user is pulling down
        if (scrollableParent.scrollTop === 0 && deltaY > 0) {
          // Only prevent if this would cause a pull-to-refresh on the main document
          // and the scrollable parent is at the top
          if (window.scrollY === 0 && document.documentElement.scrollTop === 0) {
            event.preventDefault();
          }
        }
        return; // Let the scrollable container handle its own scrolling
      }

      // Only prevent pull-to-refresh if we're at the very top of the main document
      // and the user is pulling down
      if ((window.scrollY === 0 && document.documentElement.scrollTop === 0) && deltaY > 0) {
        event.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      isDragging = false;
      startY = 0;
      currentY = 0;
    };

    // Add CSS to prevent overscroll behavior only at the document level
    const style = document.createElement('style');
    style.textContent = `
      html {
        overscroll-behavior-y: none;
        -webkit-overflow-scrolling: touch;
      }
      
      body {
        overscroll-behavior-y: none;
        -webkit-user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }
      
      /* Allow scrolling within containers */
      [style*="overflow"], 
      .scrollable, 
      .overflow-auto,
      .overflow-scroll {
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: auto;
      }
    `;
    document.head.appendChild(style);

    // Add event listeners with passive: false only for touchmove
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Cleanup: remove event listeners and styles when component unmounts
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  return null; // This component doesn't render anything
};

export default DisablePullToRefresh;